/*
 * Generate the appropriate feedback content for users.
 *
 * Feedback is based on prior user answers and the
 * MyUSE clinical decision logic.
 */

const getDefaultState = () => {
  return {
    fb_rules: null,
    fb_items: [],
    fb_content: null,
  }
}

export const state = getDefaultState()

const containsKey = (
  object,
  key
) => {
  try {
    return Object.prototype.hasOwnProperty.call(
      object,
      key
    )
  } catch (_) {
    return false
  }
}

const cloneItem = (
  item
) => {
  if (
    !item ||
    typeof item !== 'object'
  ) {
    return null
  }

  try {
    return JSON.parse(
      JSON.stringify(item)
    )
  } catch (_) {
    return {
      ...item,
    }
  }
}

/**
 * Convert an array-like value into a clean array without removing
 * meaningful falsy values.
 *
 * Important:
 *
 * - DAST answers use numeric 0 and 1.
 * - Slider answers may also legitimately be 0.
 * - filter(Boolean) must not be used because it removes numeric 0
 *   and changes the position of every answer that follows it.
 */
const normaliseArray = (
  value
) => {
  const source =
    Array.isArray(value)
      ? value
      : (
        value &&
        typeof value === 'object' &&
        Array.isArray(value.items)
          ? value.items
          : []
      )

  return source.filter(
    (item) =>
      item !== null &&
      item !== undefined &&
      item !== ''
  )
}

const getContentItems = (
  content
) => {
  if (
    content &&
    Array.isArray(content.items)
  ) {
    return content.items
  }

  return []
}

export const mutations = {
  RESET_STATE(state) {
    Object.assign(
      state,
      getDefaultState()
    )
  },

  setFeedbackItems(
    state,
    feedbackItems
  ) {
    state.fb_items =
      Array.isArray(feedbackItems)
        ? feedbackItems.filter(Boolean)
        : []
  },

  setFeedbackRules(
    state,
    rules
  ) {
    state.fb_rules =
      rules || null
  },

  setFeedbackContent(
    state,
    feedbackContent
  ) {
    state.fb_content =
      feedbackContent || null
  },
}

export const actions = {
  resetState({
    commit,
  }) {
    commit(
      'RESET_STATE'
    )
  },

  /**
   * Rebuild dynamic feedback when viewing a completed page in
   * review mode.
   */
  async rehydrateForReview({
    rootGetters,
    dispatch,
  }) {
    const page =
      rootGetters[
        'navigation/getCurrentPage'
      ]

    switch (page) {
      case '1-7':
        return dispatch(
          'processExperiences'
        )

      case '1-14':
        return dispatch(
          'processDescriptiveNorms'
        )

      case '1-15':
        return dispatch(
          'processInjunctiveNorms'
        )

      case '2-2':
        return dispatch(
          'processRelationship'
        )
        
        case '2-6':
  return dispatch(
    'processACT'
  )

      case '2C-2':
        return dispatch(
          'processNeedsFeedback'
        )

      case '2C-4':
        return dispatch(
          'processHelpFeedback'
        )

      case '4-3':
        return dispatch(
          'processReasonsForUse'
        )

      case '4A-3':
        return dispatch(
          'processInfluencesFeedback'
        )

      case '5-2':
        return dispatch(
          'processHRFeedback'
        )

      case '5A-4':
        return dispatch(
          'processSkillsFeedback'
        )

      default:
        return undefined
    }
  },

  /**
   * Process the feedback definition supplied by the current
   * Markdown page.
   *
   * The page's process_id is authoritative. Decision-tree
   * fb_rules may still be retained for older components, but
   * they must not select the processor for a different page.
   */
  async processFeedback(
    {
      commit,
      dispatch,
    },
    content
  ) {
    commit(
      'setFeedbackItems',
      []
    )

    commit(
      'setFeedbackContent',
      content || null
    )

    if (
      !content ||
      typeof content !== 'object'
    ) {
      return
    }

    const processId =
      typeof content.process_id ===
        'string'
        ? content.process_id.trim()
        : ''

    if (!processId) {
      return
    }

    await dispatch(
      processId
    )
  },

  /**
   * Process feedback where selected answers map directly to
   * Markdown items with matching data_id values.
   */
  processGenericFeedback(
    {
      commit,
      getters,
    },
    payload = {}
  ) {
    const answers =
      normaliseArray(
        payload.answers
      )

    const content =
      Array.isArray(
        payload.content
      )
        ? payload.content
        : []

    const feedbackItems = []

    answers.forEach(
      (answer) => {
        if (
          !answer ||
          !answer.data_id
        ) {
          return
        }

        const matched =
          getters.getFeedbackItem(
            answer,
            content
          )

        const feedback =
          cloneItem(matched)

        if (!feedback) {
          return
        }

        if (answer.other) {
          feedback.other =
            answer.other
        }

        feedbackItems.push(
          feedback
        )
      }
    )

    commit(
      'setFeedbackItems',
      feedbackItems
    )

    return feedbackItems
  },

  // Page 1-4
  async processDastScore({
    state,
    commit,
  }) {
    commit(
      'setFeedbackItems',
      []
    )

    try {
      const response =
        await this.$axios.$get(
          this.$config.BASE_API +
            '/processing/process_dast_score'
        )

      const items =
        getContentItems(
          state.fb_content
        )

      const selected =
        items[
          response.content_no
        ]

      commit(
        'setFeedbackItems',
        selected
          ? [selected]
          : []
      )
    } catch (error) {
      console.error(
        'DAST feedback failed:',
        error
      )

      commit(
        'setFeedbackItems',
        []
      )
    }
  },

  // Page 1-5
    /**
   * Page 1-5 — personalised DAST feedback.
   *
   * Content mapping:
   *
   * content[0] = general drug-use feedback
   * content[1] = DAST question 1 feedback
   * content[2] = DAST question 2 feedback
   * ...
   * content[9] = DAST question 9 feedback
   *
   * The general feedback card is always displayed.
   * Each additional card is based on the corresponding scoring answer.
   */
    processDASTFeedback({
      commit,
      rootGetters,
      state,
    }) {
      const content =
        getContentItems(
          state.fb_content
        )
  
      const rawAnswers =
        rootGetters[
          'profile/getDASTAnswers'
        ]
  
      const answers =
        normaliseArray(
          rawAnswers
        ).map(
          (answer) => {
            const numericAnswer =
              Number(answer)
  
            return Number.isNaN(
              numericAnswer
            )
              ? answer
              : numericAnswer
          }
        )
  
      /*
       * DAST feedback responses that contribute to the score.
       *
       * Question 1: Yes = 0
       * Question 2: No  = 1 (reverse scored)
       * Questions 3–9: Yes = 0
       *
       * Page 1-5 has nine personalised feedback cards after the
       * always-visible general card.
       */
      const feedbackAnswers = [
        0,
        1,
        0,
        0,
        0,
        0,
        0,
        0,
        0,
      ]
  
      const feedbackItems = []
  
      if (content[0]) {
        feedbackItems.push(
          cloneItem(
            content[0]
          )
        )
      }
  
      feedbackAnswers.forEach(
        (
          scoringAnswer,
          index
        ) => {
          const answer =
            answers[index]
  
          const feedbackItem =
            content[index + 1]
  
          if (
            answer ===
              scoringAnswer &&
            feedbackItem
          ) {
            feedbackItems.push(
              cloneItem(
                feedbackItem
              )
            )
          }
        }
      )
  
      commit(
        'setFeedbackItems',
        feedbackItems
      )
  
      return feedbackItems
    },

  // Page 1-4A
  async processQuizFeedback({
    state,
    commit,
  }) {
    commit(
      'setFeedbackItems',
      []
    )

    try {
      const response =
        await this.$axios.$get(
          this.$config.BASE_API +
            '/processing/process_quiz_score'
        )

      const items =
        getContentItems(
          state.fb_content
        )

      const selected =
        items[
          response.content_no
        ]

      commit(
        'setFeedbackItems',
        selected
          ? [selected]
          : []
      )
    } catch (error) {
      console.error(
        'Quiz feedback failed:',
        error
      )

      commit(
        'setFeedbackItems',
        []
      )
    }
  },

  // Page 1-7
  processExperiences({
    state,
    commit,
    rootGetters,
  }) {
    const positives =
      normaliseArray(
        rootGetters[
          'profile/getPositives'
        ]
      )

    const negatives =
      normaliseArray(
        rootGetters[
          'profile/getNegatives'
        ]
      )

    const items =
      getContentItems(
        state.fb_content
      )

    let contentIndex = 2

    if (
      positives.length >
      negatives.length
    ) {
      contentIndex = 0
    } else if (
      negatives.length >
      positives.length
    ) {
      contentIndex = 1
    }

    const selectedFeedback =
      cloneItem(
        items[contentIndex]
      )

    commit(
      'setFeedbackItems',
      selectedFeedback
        ? [selectedFeedback]
        : []
    )
  },

  /**
   * Pages 1-11A and 1-11C.
   *
   * Feedback depends on willingness to change:
   *
   * 0 or 1 -> item 0
   * 2      -> item 1
   * 3 or 4 -> item 2
   */
  processConfidence({
    state,
    commit,
    rootGetters,
  }) {
    const willingness =
      Number(
        rootGetters[
          'profile/getWillingnessToChangeScore'
        ]
      )

    const items =
      getContentItems(
        state.fb_content
      )

    let contentIndex = 0

    if (
      Number.isFinite(
        willingness
      )
    ) {
      if (
        willingness >= 3
      ) {
        contentIndex = 2
      } else if (
        willingness === 2
      ) {
        contentIndex = 1
      }
    }

    const selectedFeedback =
      cloneItem(
        items[contentIndex]
      )

    commit(
      'setFeedbackItems',
      selectedFeedback
        ? [selectedFeedback]
        : []
    )
  },

  // Page 1-14
  async processDescriptiveNorms({
    rootGetters,
    commit,
  }) {
    const userScores =
      normaliseArray(
        rootGetters[
          'profile/getDescriptiveNorms'
        ]
      )

    const correctAnswers = [
      43,
      34,
    ]

    const errorMargin = 5
    const feedbackItems = []

    for (
      let index = 0;
      index <
      correctAnswers.length;
      index += 1
    ) {
      const userAnswer =
        Number(
          userScores[index]
        ) || 0

      const correctAnswer =
        correctAnswers[index]

      const difference =
        Math.abs(
          userAnswer -
          correctAnswer
        )

      feedbackItems.push({
        status:
          difference <=
          errorMargin
            ? 1
            : 0,

        scores: [
          correctAnswer,
          userAnswer,
        ],
      })
    }

    commit(
      'setFeedbackItems',
      feedbackItems
    )

    try {
      await this.$axios.$post(
        this.$config.BASE_API +
          '/activity/commit_score',
        {
          value:
            userScores.toString(),

          type: 9,
        }
      )
    } catch (_) {
      /**
       * Server persistence must not remove visible feedback.
       */
    }
  },

  // Page 1-15
  processInjunctiveNorms({
    state,
    rootGetters,
    commit,
    dispatch,
    getters,
  }) {
    const answers =
      normaliseArray(
        rootGetters[
          'profile/getInjunctiveNorms'
        ]
      )

    const userType =
      Number(
        rootGetters[
          'profile/getUserType'
        ]
      )

    const content =
      getContentItems(
        state.fb_content
      )

    const feedbackItems = []

    const nextPage =
      userType === 1
        ? '2-0A'
        : '2-0'

    const patterns = [
      {
        pattern: [
          0,
          4,
          7,
        ],
        overall: 0,
      },
      {
        pattern: [
          1,
          3,
          6,
        ],
        overall: 1,
      },
      {
        pattern: [
          2,
          5,
          8,
        ],
        overall: 2,
      },
    ]

    const matchPattern = (
      pattern,
      answerList
    ) => {
      if (
        !Array.isArray(
          answerList
        ) ||
        answerList.length !==
          pattern.length
      ) {
        return false
      }

      return answerList.every(
        (
          answer,
          index
        ) => {
          return (
            answer &&
            answer.data_id ===
              `injunctive-norm-${pattern[index]}`
          )
        }
      )
    }

    answers.forEach(
      (answer) => {
        const item =
          getters.getFeedbackItem(
            answer,
            content
          )

        const cloned =
          cloneItem(item)

        if (cloned) {
          feedbackItems.push(
            cloned
          )
        }
      }
    )

    let overallId =
      'injunctive-norm-overall-2'

    for (
      const pattern of
        patterns
    ) {
      if (
        matchPattern(
          pattern.pattern,
          answers
        )
      ) {
        overallId =
          `injunctive-norm-overall-${pattern.overall}`

        break
      }
    }

    const overall =
      cloneItem(
        getters.getFeedbackItem(
          {
            data_id:
              overallId,
          },
          content
        )
      )

    if (overall) {
      feedbackItems.push(
        overall
      )
    }

    commit(
      'setFeedbackItems',
      feedbackItems
    )

    return dispatch(
      'navigation/setNextPage',
      nextPage,
      {
        root: true,
      }
    )
  },

  // Page 2-2
  processRelationship({
    state,
    rootGetters,
    commit,
  }) {
    const scores =
      normaliseArray(
        rootGetters[
          'profile/getRelationshipScores'
        ]
      ).map(
        (value) =>
          Number(value) || 0
      )

    const content =
      getContentItems(
        state.fb_content
      )

    const feedbackItems = []

    const total =
      scores.reduce(
        (
          sum,
          value
        ) =>
          sum + value,
        0
      )

    const getFeedback = (
      answer,
      highItem,
      lowItem
    ) => {
      return answer >= 5
        ? highItem
        : lowItem
    }

    const selectedItems = [
      getFeedback(
        scores[0] || 0,
        content[0],
        content[1]
      ),
      getFeedback(
        scores[1] || 0,
        content[2],
        content[3]
      ),
      getFeedback(
        scores[2] || 0,
        content[4],
        content[5]
      ),
    ]

    selectedItems.forEach(
      (item) => {
        const cloned =
          cloneItem(item)

        if (cloned) {
          feedbackItems.push(
            cloned
          )
        }
      }
    )

    if (
      total > 5 &&
      content[6]
    ) {
      feedbackItems.push(
        cloneItem(
          content[6]
        )
      )
    }

    commit(
      'setFeedbackItems',
      feedbackItems
    )
  },

  // Page 2-4
  async processBehaviourFeedback({
    state,
    commit,
  }) {
    commit(
      'setFeedbackItems',
      []
    )

    try {
      const response =
        await this.$axios.$get(
          this.$config.BASE_API +
            '/processing/process_behaviour_feedback'
        )

      const items =
        getContentItems(
          state.fb_content
        )

      const selected =
        cloneItem(
          items[
            response.content_no
          ]
        )

      commit(
        'setFeedbackItems',
        selected
          ? [selected]
          : []
      )
    } catch (_) {
      commit(
        'setFeedbackItems',
        []
      )
    }
  },

  // Page 2-6
  async processACT({
    state,
    commit,
  }) {
    commit(
      'setFeedbackItems',
      []
    )

    try {
      const response =
        await this.$axios.$get(
          this.$config.BASE_API +
            '/processing/process_behavioural_awareness'
        )

      const items =
        getContentItems(
          state.fb_content
        )

      const selected =
        cloneItem(
          items[
            response.content_no
          ]
        )

      commit(
        'setFeedbackItems',
        selected
          ? [selected]
          : []
      )
    } catch (_) {
      commit(
        'setFeedbackItems',
        []
      )
    }
  },

  // Pages 2-8 and 5A-1
  async processDecisionMaking({
    state,
    commit,
  }) {
    commit(
      'setFeedbackItems',
      []
    )

    try {
      const response =
        await this.$axios.$get(
          this.$config.BASE_API +
            '/processing/process_decision_making_score'
        )

      const items =
        getContentItems(
          state.fb_content
        )

      const selected =
        cloneItem(
          items[
            response.content_no
          ]
        )

      commit(
        'setFeedbackItems',
        selected
          ? [selected]
          : []
      )
    } catch (error) {
      console.error(
        'Decision-making feedback failed:',
        error
      )

      commit(
        'setFeedbackItems',
        []
      )
    }
  },

  // Page 2-9
  async processFullyPresentMetaphor({
    rootGetters,
    dispatch,
  }) {
    const highACTScore =
      Number(
        rootGetters[
          'profile/getBehaviouralAwarenessScore'
        ]
      ) >= 17

    const userType =
      Number(
        rootGetters[
          'profile/getUserType'
        ]
      )

    const drugUser =
      userType !== 1

    const highDecisionScore =
      Number(
        rootGetters[
          'profile/getDecisionMakingScore'
        ]
      ) >= 32

    const lowLifeValuesScore =
      Number(
        rootGetters[
          'profile/getLifeValuesScore'
        ]
      ) <= 10

    if (lowLifeValuesScore) {
      await dispatch(
        'setNextPage',
        '3-0'
      )

      return
    }

    const unlocked = [
      '3',
    ]

    const canSkipSkillsModule =
      highACTScore &&
      highDecisionScore

    if (drugUser) {
      if (
        canSkipSkillsModule
      ) {
        await dispatch(
          'setNextPage',
          '5-0'
        )

        unlocked.push(
          '4'
        )
      } else {
        await dispatch(
          'setNextPage',
          '4-0'
        )
      }
    } else if (
      canSkipSkillsModule
    ) {
      await dispatch(
        'setNextPage',
        'Review'
      )

      unlocked.push(
        '4A'
      )
    } else {
      await dispatch(
        'setNextPage',
        '4A-0'
      )
    }

    await dispatch(
      'navigation/unlockOptionalModules',
      unlocked,
      {
        root: true,
      }
    )
  },

  // Page 2C-2
  processNeedsFeedback({
    state,
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'processGenericFeedback',
      {
        answers:
          rootGetters[
            'profile/getNeeds'
          ],

        content:
          getContentItems(
            state.fb_content
          ),
      }
    )
  },

  // Page 2C-4
  processHelpFeedback({
    state,
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'processGenericFeedback',
      {
        answers:
          rootGetters[
            'profile/getHelps'
          ],

        content:
          getContentItems(
            state.fb_content
          ),
      }
    )
  },

  // Page 4A-3
  processInfluencesFeedback({
    state,
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'processGenericFeedback',
      {
        answers:
          rootGetters[
            'profile/getInfluences'
          ],

        content:
          getContentItems(
            state.fb_content
          ),
      }
    )
  },

  // Page 4-3
  processReasonsForUse({
    state,
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'processGenericFeedback',
      {
        answers:
          rootGetters[
            'profile/getReasonsForUse'
          ],

        content:
          getContentItems(
            state.fb_content
          ),
      }
    )
  },

  // Page 4-8
  processMyDecisions({
    state,
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'processGenericFeedback',
      {
        answers:
          rootGetters[
            'profile/getNegatives'
          ],

        content:
          getContentItems(
            state.fb_content
          ),
      }
    )
  },

  // Page 4-9
  processMyInfluences({
    rootGetters,
    dispatch,
  }) {
    return dispatch(
      'setNextPage',
      rootGetters[
        'navigation/getOptionalModStatus'
      ]
        ? 'dashboard'
        : '5-0'
    )
  },

  // Page 5-2
  processHRFeedback({
    state,
    rootGetters,
    commit,
    getters,
  }) {
    const answers =
      normaliseArray(
        rootGetters[
          'profile/getHRPractices'
        ]
      )

    const content =
      getContentItems(
        state.fb_content
      )

    const feedbackItems = []

    answers.forEach(
      (answer) => {
        const matched =
          getters.getFeedbackItem(
            answer,
            content
          )

        const item =
          cloneItem(matched)

        if (!item) {
          return
        }

        if (
          answer.data_id ===
          'hr-practice-7'
        ) {
          item.recall = [
            'InjunctiveNorms',
          ]
        } else if (
          answer.data_id ===
          'hr-practice-8'
        ) {
          item.recall = [
            'ReasonsForUse',
            'DarraghHooks',
          ]
        }

        feedbackItems.push(
          item
        )
      }
    )

    commit(
      'setFeedbackItems',
      feedbackItems
    )
  },

  // Page 5A-4
  processSkillsFeedback({
    state,
    rootGetters,
    commit,
    getters,
  }) {
    const answers =
      normaliseArray(
        rootGetters[
          'profile/getSkills'
        ]
      )

    const content =
      getContentItems(
        state.fb_content
      )

    const feedbackItems = []

    answers.forEach(
      (answer) => {
        const matched =
          getters.getFeedbackItem(
            answer,
            content
          )

        const item =
          cloneItem(matched)

        if (!item) {
          return
        }

        if (
          answer.data_id ===
          'skill-7'
        ) {
          item.recall = [
            'InjunctiveNorms',
          ]
        } else if (
          answer.data_id ===
          'skill-8'
        ) {
          item.recall = [
            'ReasonsForUse',
            'DarraghHooks',
          ]
        }

        feedbackItems.push(
          item
        )
      }
    )

    commit(
      'setFeedbackItems',
      feedbackItems
    )
  },

  // Page 3-8
  async processValuesFeedback({
    rootGetters,
    dispatch,
  }) {
    const highDecisionScore =
      Number(
        rootGetters[
          'profile/getDecisionMakingScore'
        ]
      ) > 32

    const highACTScore =
      Number(
        rootGetters[
          'profile/getBehaviouralAwarenessScore'
        ]
      ) >= 17

    const userType =
      Number(
        rootGetters[
          'profile/getUserType'
        ]
      )

    const drugUser =
      userType !== 1

    if (
      highACTScore ||
      highDecisionScore
    ) {
      await dispatch(
        'setNextPage',
        drugUser
          ? '4-0'
          : '4A-0'
      )

      return
    }

    await dispatch(
      'setNextPage',
      drugUser
        ? '5-0'
        : '5A-0'
    )

    await dispatch(
      'navigation/unlockOptionalModules',
      drugUser
        ? ['4']
        : ['4A'],
      {
        root: true,
      }
    )
  },

  setNextPage({
    dispatch,
  }, nextPage) {
    return dispatch(
      'navigation/setNextPage',
      nextPage,
      {
        root: true,
      }
    )
  },
}

export const getters = {
  getFeedbackItems(
    state
  ) {
    return Array.isArray(
      state.fb_items
    )
      ? state.fb_items
      : []
  },

  getFeedbackItem:
    () =>
    (
      answer,
      content
    ) => {
      if (
        !answer ||
        !answer.data_id ||
        !Array.isArray(content)
      ) {
        return null
      }

      return (
        content.find(
          (item) => {
            return (
              item &&
              item.data_id ===
                answer.data_id
            )
          }
        ) || null
      )
    },
}

export const helpers = {
  clone(data) {
    return cloneItem(data)
  },

  getItem(
    answer,
    content
  ) {
    if (
      !answer ||
      !answer.data_id ||
      !Array.isArray(content)
    ) {
      return null
    }

    return (
      content.find(
        (item) => {
          return (
            item &&
            item.data_id ===
              answer.data_id
          )
        }
      ) || null
    )
  },

  async storeActivityOnServer(
    item,
    type,
    $axios,
    $config
  ) {
    await $axios.$post(
      $config.BASE_API +
        '/activity/commit_activity',
      {
        value:
          JSON.stringify(item),

        type,
      }
    )
  },

  async storeScoreOnServer(
    value,
    type,
    $axios,
    $config
  ) {
    await $axios.$post(
      $config.BASE_API +
        '/activity/commit_score',
      {
        value,
        type,
      }
    )
  },
}